import type { DeviceSetting, DeviceSettingInput } from '@/api/sessionNotes/sessionNotesApi.types';
import { isRecordableDeviceSettingValue } from '@/utils/durationFormatting';

export type DeviceSettingSet = Record<string, string>;

export function createEmptyDeviceSettingSets(): DeviceSettingSet[] {
  return [{}];
}

export function groupDeviceSettingsIntoSets(
  settings: DeviceSetting[],
  toEditableValue: (setting: DeviceSetting, deviceSettingFieldId: string) => string
): DeviceSettingSet[] {
  const setsByNumber = new Map<number, DeviceSettingSet>();
  for (const setting of settings) {
    const deviceSettingFieldId = setting.deviceSettingFieldId;
    if (deviceSettingFieldId === null) {
      continue;
    }
    const existingSet = setsByNumber.get(setting.setNumber);
    const settingSet = existingSet === undefined ? {} : existingSet;
    settingSet[deviceSettingFieldId] = toEditableValue(setting, deviceSettingFieldId);
    setsByNumber.set(setting.setNumber, settingSet);
  }
  const orderedSets = [...setsByNumber.entries()]
    .sort(function bySetNumber(first, second) {
      return first[0] - second[0];
    })
    .map(function toSet(entry) {
      return entry[1];
    });
  return orderedSets.length === 0 ? createEmptyDeviceSettingSets() : orderedSets;
}

function recordableEntries(settingSet: DeviceSettingSet): [string, string][] {
  return Object.entries(settingSet).filter(function isRecordable(entry) {
    return isRecordableDeviceSettingValue(entry[1]);
  });
}

export function flattenDeviceSettingSets(sets: DeviceSettingSet[]): DeviceSettingInput[] {
  const filledSets = sets.map(recordableEntries).filter(function hasValues(entries) {
    return entries.length > 0;
  });
  return filledSets.flatMap(function toInputs(entries, setIndex) {
    return entries.map(function toInput(entry) {
      return { deviceSettingFieldId: entry[0], value: entry[1], setNumber: setIndex + 1 };
    });
  });
}
